import { Injectable } from "@angular/core";
import { Observable, catchError, shareReplay, throwError } from "rxjs";

interface CacheEntry {
  expiresAt: number;
  response$: Observable<any>;
}

/**
 * In-memory cache for GET responses, shared by the whole app.
 * Concurrent requests with the same key share a single HTTP call, and
 * failed responses are never kept.
 */
@Injectable({
  providedIn: "root",
})
export class RequestCache {
  static readonly DEFAULT_TTL_MS = 5 * 60 * 1000;

  private readonly entries = new Map<string, CacheEntry>();

  get<T>(
    key: string,
    ttlMs: number,
    request: () => Observable<T>
  ): Observable<T> {
    const entry = this.entries.get(key);
    if (entry && entry.expiresAt > Date.now()) {
      return entry.response$;
    }

    const response$: Observable<T> = request().pipe(
      catchError((error) => {
        if (this.entries.get(key)?.response$ === response$) {
          this.entries.delete(key);
        }
        return throwError(() => error);
      }),
      shareReplay(1)
    );
    this.entries.set(key, { expiresAt: Date.now() + ttlMs, response$ });
    return response$;
  }

  clear(): void {
    this.entries.clear();
  }
}
